import { useState, useEffect, useMemo } from 'react';
import type { ChangeEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Typography,
  Grid,
  Switch,
  TextField,
  MenuItem,
  IconButton,
  Alert,
  CircularProgress,
  Paper,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import {
  doc,
  getDoc,
  setDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useTenant } from '../providers/TenantProvider';
import type { ProviderSchedule, DaySchedule, TimeRange } from '../lib/db/types';

// Common timezones
const COMMON_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'America/Honolulu',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Moscow',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Dubai',
  'Asia/Singapore',
  'Australia/Sydney',
  'Pacific/Auckland',
  'UTC',
];

// Days of the week
const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
] as const;

type DayKey = typeof DAYS_OF_WEEK[number]['key'];

interface Provider {
  id: string;
  firstName?: string;
  lastName?: string;
  email: string;
}

const defaultWorkingHours: DaySchedule = {
  isWorking: true,
  start: '09:00',
  end: '17:00',
  breaks: [],
};

const emptyDaySchedule: DaySchedule | null = null;

export function ProviderSchedulePage() {
  const { providerId } = useParams<{ providerId: string }>();
  const navigate = useNavigate();
  const { tenantId, role } = useTenant();
  const scheduleDocId = tenantId && providerId ? `${tenantId}_${providerId}` : '';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [provider, setProvider] = useState<Provider | null>(null);
  const [schedule, setSchedule] = useState<ProviderSchedule | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Exception dialog state
  const [exceptionDialogOpen, setExceptionDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [exceptionForm, setExceptionForm] = useState<{
    isWorking: boolean;
    start: string;
    end: string;
    breaks: TimeRange[];
  }>({
    isWorking: false,
    start: '09:00',
    end: '17:00',
    breaks: [],
  });

  const canEdit = role === 'owner' || role === 'manager';

  // Initialize default schedule
  const defaultSchedule: Partial<ProviderSchedule> = {
    workingPlan: {
      monday: { ...defaultWorkingHours },
      tuesday: { ...defaultWorkingHours },
      wednesday: { ...defaultWorkingHours },
      thursday: { ...defaultWorkingHours },
      friday: { ...defaultWorkingHours },
      saturday: emptyDaySchedule,
      sunday: emptyDaySchedule,
    },
    exceptions: {},
    timezone: 'America/New_York',
  };

  // Load provider and schedule
  useEffect(() => {
    if (!tenantId || !providerId) return;
    loadData();
  }, [tenantId, providerId]);

  const loadData = async () => {
    if (!tenantId || !providerId) return;
    
    try {
      setLoading(true);
      setMessage(null);

      // Load provider info
      const providerDoc = await getDoc(doc(db, 'tenants', tenantId, 'memberships', providerId));
      if (providerDoc.exists()) {
        const data = providerDoc.data();
        setProvider({
          id: providerId,
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
        });
      }

      // Load schedule
      const scheduleRef = doc(db, 'providerSchedules', scheduleDocId);
      const scheduleDoc = await getDoc(scheduleRef);

      if (scheduleDoc.exists()) {
        const data = scheduleDoc.data() as ProviderSchedule;
        setSchedule(data);
      } else {
        // No schedule yet, use defaults
        setSchedule({
          ...defaultSchedule,
          tenantId,
          providerId,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        } as ProviderSchedule);
      }
    } catch (error: any) {
      console.error('Error loading schedule:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to load schedule' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSchedule = async () => {
    if (!tenantId || !providerId || !schedule) return;

    try {
      setSaving(true);
      setMessage(null);

      const scheduleData = {
        ...schedule,
        tenantId,
        providerId,
        updatedAt: Timestamp.now(),
      };

      if (!schedule.createdAt) {
        scheduleData.createdAt = Timestamp.now();
      }

      await setDoc(doc(db, 'providerSchedules', scheduleDocId), scheduleData);
      setMessage({ type: 'success', text: 'Schedule saved successfully' });
    } catch (error: any) {
      console.error('Error saving schedule:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to save schedule' });
    } finally {
      setSaving(false);
    }
  };

  const updateDaySchedule = (day: DayKey, updates: Partial<DaySchedule> | null) => {
    if (!schedule) return;

    setSchedule({
      ...schedule,
      workingPlan: {
        ...schedule.workingPlan,
        [day]: updates,
      },
    });
  };

  const toggleDayWorking = (day: DayKey) => {
    const currentSchedule = schedule?.workingPlan[day];
    if (currentSchedule) {
      updateDaySchedule(day, null);
    } else {
      updateDaySchedule(day, { ...defaultWorkingHours });
    }
  };

  const updateDayTime = (day: DayKey, field: 'start' | 'end', value: string) => {
    const currentSchedule = schedule?.workingPlan[day];
    if (!currentSchedule) return;

    updateDaySchedule(day, {
      ...currentSchedule,
      [field]: value,
    });
  };

  const addBreak = (day: DayKey) => {
    const currentSchedule = schedule?.workingPlan[day];
    if (!currentSchedule) return;

    updateDaySchedule(day, {
      ...currentSchedule,
      breaks: [...currentSchedule.breaks, { start: '12:00', end: '13:00' }],
    });
  };

  const removeBreak = (day: DayKey, index: number) => {
    const currentSchedule = schedule?.workingPlan[day];
    if (!currentSchedule) return;

    updateDaySchedule(day, {
      ...currentSchedule,
      breaks: currentSchedule.breaks.filter((_, i) => i !== index),
    });
  };

  const updateBreak = (day: DayKey, index: number, field: 'start' | 'end', value: string) => {
    const currentSchedule = schedule?.workingPlan[day];
    if (!currentSchedule) return;

    const newBreaks = [...currentSchedule.breaks];
    newBreaks[index] = { ...newBreaks[index], [field]: value };

    updateDaySchedule(day, {
      ...currentSchedule,
      breaks: newBreaks,
    });
  };

  // Exception management
  const openExceptionDialog = (date: string) => {
    const existingException = schedule?.exceptions?.[date];
    
    if (existingException) {
      setExceptionForm({
        isWorking: existingException.isWorking,
        start: existingException.start || '09:00',
        end: existingException.end || '17:00',
        breaks: existingException.breaks || [],
      });
    } else {
      setExceptionForm({
        isWorking: false,
        start: '09:00',
        end: '17:00',
        breaks: [],
      });
    }
    
    setSelectedDate(date);
    setExceptionDialogOpen(true);
  };

  const saveException = () => {
    if (!schedule || !selectedDate) return;

    const exceptionData: DaySchedule | null = exceptionForm.isWorking
      ? {
          isWorking: true,
          start: exceptionForm.start,
          end: exceptionForm.end,
          breaks: exceptionForm.breaks,
        }
      : { isWorking: false, start: '', end: '', breaks: [] };

    setSchedule({
      ...schedule,
      exceptions: {
        ...schedule.exceptions,
        [selectedDate]: exceptionData,
      },
    });

    setExceptionDialogOpen(false);
    setSelectedDate('');
  };

  const deleteException = (date: string) => {
    if (!schedule) return;

    const { [date]: _, ...remainingExceptions } = schedule.exceptions;
    
    setSchedule({
      ...schedule,
      exceptions: remainingExceptions,
    });
  };

  const addExceptionBreak = () => {
    setExceptionForm({
      ...exceptionForm,
      breaks: [...exceptionForm.breaks, { start: '12:00', end: '13:00' }],
    });
  };

  const removeExceptionBreak = (index: number) => {
    setExceptionForm({
      ...exceptionForm,
      breaks: exceptionForm.breaks.filter((_, i) => i !== index),
    });
  };

  const updateExceptionBreak = (index: number, field: 'start' | 'end', value: string) => {
    const newBreaks = [...exceptionForm.breaks];
    newBreaks[index] = { ...newBreaks[index], [field]: value };
    setExceptionForm({ ...exceptionForm, breaks: newBreaks });
  };

  // Generate calendar days for current month
  const calendarDays = useMemo(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay(); // 0 = Sunday
    
    const days: { date: string; dayOfMonth: number; isCurrentMonth: boolean }[] = [];
    
    // Previous month padding
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push({ date: '', dayOfMonth: 0, isCurrentMonth: false });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      const date = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
      days.push({ date, dayOfMonth: i, isCurrentMonth: true });
    }
    
    return days;
  }, []);

  const monthName = new Date().toLocaleString('default', { month: 'long', year: 'numeric' });

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!provider) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">Provider not found</Alert>
      </Container>
    );
  }

  const providerName = provider.firstName || provider.lastName
    ? `${provider.firstName || ''} ${provider.lastName || ''}`.trim()
    : provider.email;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/users')}
          sx={{ mb: 2 }}
        >
          Back to Users
        </Button>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" component="h1">
              Provider Schedule
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
              {providerName}
            </Typography>
          </Box>
          {canEdit && (
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSaveSchedule}
              disabled={saving}
            >
              Save Schedule
            </Button>
          )}
        </Box>
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* Timezone Selector */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Timezone
          </Typography>
          <TextField
            select
            fullWidth
            value={schedule?.timezone || 'America/New_York'}
            onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
              if (schedule) {
                setSchedule({ ...schedule, timezone: e.target.value });
              }
            }}
            disabled={!canEdit}
            helperText="All times are in this timezone"
          >
            {COMMON_TIMEZONES.map((tz) => (
              <MenuItem key={tz} value={tz}>
                {tz}
              </MenuItem>
            ))}
          </TextField>
        </CardContent>
      </Card>

      {/* Weekly Working Hours */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Weekly Working Hours
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Set the default schedule for each day of the week
          </Typography>

          <Grid container spacing={3}>
            {DAYS_OF_WEEK.map(({ key, label }) => {
              const daySchedule = schedule?.workingPlan[key];
              const isWorking = !!daySchedule;

              return (
                <Grid item xs={12} key={key}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      flexWrap="wrap"
                      gap={2}
                    >
                      <Box display="flex" alignItems="center" gap={2}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={isWorking}
                              onChange={() => toggleDayWorking(key)}
                              disabled={!canEdit}
                            />
                          }
                          label={
                            <Typography variant="subtitle1" fontWeight={500}>
                              {label}
                            </Typography>
                          }
                        />
                      </Box>

                      {isWorking && daySchedule && (
                        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                          <TextField
                            label="Start"
                            type="time"
                            value={daySchedule.start}
                            onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => updateDayTime(key, 'start', e.target.value)}
                            disabled={!canEdit}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ step: 300 }} // 5 minute intervals
                            sx={{ width: 120 }}
                          />
                          <TextField
                            label="End"
                            type="time"
                            value={daySchedule.end}
                            onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => updateDayTime(key, 'end', e.target.value)}
                            disabled={!canEdit}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ step: 300 }}
                            sx={{ width: 120 }}
                          />
                        </Box>
                      )}

                      {!isWorking && (
                        <Chip label="Not Working" size="small" color="default" />
                      )}
                    </Box>

                    {/* Breaks */}
                    {isWorking && daySchedule && (
                      <Box sx={{ mt: 2, pl: 7 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Breaks:
                        </Typography>
                        {daySchedule.breaks.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No breaks set
                          </Typography>
                        ) : (
                          <Stack spacing={1} alignItems="flex-start">
                            {daySchedule.breaks.map((breakTime, index) => (
                              <Box key={index} display="flex" alignItems="center" gap={1}>
                                <TextField
                                  type="time"
                                  value={breakTime.start}
                                  onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                                    updateBreak(key, index, 'start', e.target.value)
                                  }
                                  disabled={!canEdit}
                                  InputLabelProps={{ shrink: true }}
                                  inputProps={{ step: 300 }}
                                  size="small"
                                  sx={{ width: 120 }}
                                />
                                <Typography variant="body2">to</Typography>
                                <TextField
                                  type="time"
                                  value={breakTime.end}
                                  onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                                    updateBreak(key, index, 'end', e.target.value)
                                  }
                                  disabled={!canEdit}
                                  InputLabelProps={{ shrink: true }}
                                  inputProps={{ step: 300 }}
                                  size="small"
                                  sx={{ width: 120 }}
                                />
                                {canEdit && (
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => removeBreak(key, index)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>
                            ))}
                          </Stack>
                        )}
                        {canEdit && (
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => addBreak(key)}
                            sx={{ mt: 1 }}
                          >
                            Add Break
                          </Button>
                        )}
                      </Box>
                    )}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>

      {/* Exceptions Calendar */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Exceptions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Add special dates like holidays or modified hours. Click a date to add an exception.
          </Typography>

          {/* Calendar Grid */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" align="center" sx={{ mb: 2 }}>
              {monthName}
            </Typography>
            <Grid container columns={7} spacing={1}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <Grid item xs={1} key={day}>
                  <Typography variant="subtitle2" align="center" color="text.secondary">
                    {day}
                  </Typography>
                </Grid>
              ))}
              {calendarDays.map((day, index) => (
                <Grid item xs={1} key={index}>
                  {day.isCurrentMonth ? (
                    <Paper
                      variant="outlined"
                      onClick={() => canEdit && openExceptionDialog(day.date)}
                      sx={{
                        p: 1,
                        textAlign: 'center',
                        cursor: canEdit ? 'pointer' : 'default',
                        '&:hover': canEdit ? { bgcolor: 'action.hover' } : {},
                        minHeight: 60,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                      }}
                    >
                      <Typography variant="body2">{day.dayOfMonth}</Typography>
                      {schedule?.exceptions?.[day.date] && (
                        <Chip
                          size="small"
                          color={schedule.exceptions[day.date]?.isWorking ? 'warning' : 'error'}
                          label={schedule.exceptions[day.date]?.isWorking ? 'Special' : 'Off'}
                          sx={{ mt: 0.5, fontSize: '10px', height: 20 }}
                        />
                      )}
                    </Paper>
                  ) : (
                    <Box sx={{ minHeight: 60 }} />
                  )}
                </Grid>
              ))}
            </Grid>
          </Paper>

          {/* Exceptions List */}
          <Typography variant="subtitle1" gutterBottom>
            Upcoming Exceptions
          </Typography>
          {Object.keys(schedule?.exceptions || {}).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No exceptions set for this month
            </Typography>
          ) : (
            <Stack spacing={1}>
              {Object.entries(schedule?.exceptions || {})
                .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
                .map(([date, exception]) => (
                  <Paper
                    key={date}
                    variant="outlined"
                    sx={{
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 1,
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <EventIcon color="action" />
                      <Typography variant="body1">
                        {new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </Typography>
                      <Chip
                        size="small"
                        color={exception?.isWorking ? 'warning' : 'error'}
                        label={exception?.isWorking ? 'Special Hours' : 'Unavailable'}
                      />
                      {exception?.isWorking && (
                        <Typography variant="body2" color="text.secondary">
                          {exception.start} - {exception.end}
                          {exception.breaks?.length > 0 &&
                            ` (${exception.breaks.length} break${exception.breaks.length > 1 ? 's' : ''})`}
                        </Typography>
                      )}
                    </Box>
                    {canEdit && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => deleteException(date)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Paper>
                ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Exception Dialog */}
      <Dialog
        open={exceptionDialogOpen}
        onClose={() => setExceptionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedDate
            ? new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
              })
            : 'Exception'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={exceptionForm.isWorking}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setExceptionForm({ ...exceptionForm, isWorking: e.target.checked })
                  }
                />
              }
              label={exceptionForm.isWorking ? 'Working (Special Hours)' : 'Not Working'}
            />

            {exceptionForm.isWorking && (
              <Box sx={{ mt: 2 }}>
                <Box display="flex" gap={2} sx={{ mb: 2 }}>
                  <TextField
                    label="Start Time"
                    type="time"
                    value={exceptionForm.start}
                    onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      setExceptionForm({ ...exceptionForm, start: e.target.value })
                    }
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                    fullWidth
                  />
                  <TextField
                    label="End Time"
                    type="time"
                    value={exceptionForm.end}
                    onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      setExceptionForm({ ...exceptionForm, end: e.target.value })
                    }
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                    fullWidth
                  />
                </Box>

                <Typography variant="subtitle2" gutterBottom>
                  Breaks
                </Typography>
                {exceptionForm.breaks.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    No breaks
                  </Typography>
                ) : (
                  <Stack spacing={1} sx={{ mb: 2 }}>
                    {exceptionForm.breaks.map((breakTime, index) => (
                      <Box key={index} display="flex" alignItems="center" gap={1}>
                        <TextField
                          type="time"
                          value={breakTime.start}
                          onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => updateExceptionBreak(index, 'start', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 300 }}
                          size="small"
                          sx={{ width: 120 }}
                        />
                        <Typography variant="body2">to</Typography>
                        <TextField
                          type="time"
                          value={breakTime.end}
                          onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => updateExceptionBreak(index, 'end', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 300 }}
                          size="small"
                          sx={{ width: 120 }}
                        />
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeExceptionBreak(index)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Stack>
                )}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={addExceptionBreak}
                >
                  Add Break
                </Button>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExceptionDialogOpen(false)}>Cancel</Button>
          <Button onClick={saveException} variant="contained">
            Save Exception
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
